import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { findFirstFocusable, findLastFocusable } from './utilities/focusable';

type Button = {
	open: () => void;
	close: () => void;
	id: string;
};

let openGroup = false;
let clickedButton: HTMLElement;

/**
 * Create a button
 */

const createPopup = (
	button: HTMLElement,
	getButtons: () => Button[],
	stage: HTMLElement,
	totalButtons: number,
): Button | undefined => {
	const selector = button.getAttribute('data-popup-button');
	if (!selector) return;

	const id = selector;

	const template = document.querySelector(selector);
	if (!template) return;

	let isOpen = false;

	const popupEl = document.createElement('div');
	popupEl.classList.add('popup');
	popupEl.setAttribute('data-popup', '');

	const createPopupElement = (content: DocumentFragment) => {
		const button = document.createElement('button');
		button.setAttribute('class', 'popup__close-button');
		button.setAttribute('data-popup-close', '');
		button.innerHTML = `<svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M21 1L1 21" stroke="black" stroke-width="2"/>
								<path d="M21 21L1 1" stroke="black" stroke-width="2"/>
							</svg>`;

		popupEl.appendChild(button);
		popupEl.appendChild(content);
		popupEl.classList.add(selector);

		stage.appendChild(popupEl);

		stage.style.display = 'grid';
		disableBodyScroll(popupEl);
	};

	const open = () => {
		getButtons().forEach((b) => b.close());

		if (isOpen) return;
		if (template && template instanceof HTMLTemplateElement) {
			const content = template.content.cloneNode(true) as DocumentFragment;
			createPopupElement(content);
			isOpen = true;
			moveFocus();
		}
	};

	const close = (closeGroup: boolean = false) => {
		if (!isOpen) return;
		enableBodyScroll(popupEl);
		while (popupEl.firstChild) {
			popupEl.removeChild(popupEl.firstChild);
		}
		popupEl.parentNode?.removeChild(popupEl);
		isOpen = false;
		if (closeGroup) {
			openGroup = false;
			stage.style.display = 'none';
			moveFocus();
			return;
		}
	};

	const moveFocus = () => {
		if (isOpen && totalButtons <= 1) {
			findFirstFocusable(popupEl)?.focus();
		} else if (isOpen && !openGroup) {
			findFirstFocusable(stage)?.focus();
			openGroup = true;
		} else if (!isOpen) {
			clickedButton.focus();
		}
	};

	button.addEventListener('click', () => {
		if (isOpen) {
			close();
		} else {
			clickedButton = button;
			open();
		}
	});

	stage.addEventListener('click', (event) => {
		if (
			event.target &&
			event.target instanceof Element &&
			event.target.hasAttribute('data-popup-close')
		) {
			close(true);
		}
	});

	/**
	 * Add behaviors for when the user presses a key
	 */
	window.addEventListener('keydown', (event: KeyboardEvent) => {
		if (!isOpen) return;
		// Close when escape is pressed
		if (
			event.key === 'Escape' &&
			event.target instanceof Element &&
			stage.contains(event.target)
		) {
			close(true);
		}
		// Lock focus within
		if (event.key === 'Tab') {
			const firstFocusableEl = findFirstFocusable(stage);
			const lastFocusableEl = findLastFocusable(stage);
			if (event.shiftKey) {
				if (document.activeElement === firstFocusableEl) {
					lastFocusableEl?.focus();
					event.preventDefault();
				}
			} else {
				if (document.activeElement === lastFocusableEl) {
					firstFocusableEl?.focus();
					event.preventDefault();
				}
			}
		}
	});

	document.addEventListener('click', (event: MouseEvent) => {
		if (
			event.target === button ||
			(event.target instanceof Element && button.contains(event.target)) ||
			(event.target instanceof Element &&
				event.target.hasAttribute('data-button-next')) ||
			(event.target instanceof Element &&
				event.target.hasAttribute('data-button-previous'))
		)
			return;
		if (
			isOpen &&
			event.target instanceof Element &&
			!popupEl.contains(event.target)
		) {
			close(true);
		}
	});

	return {
		open,
		close,
		id,
	};
};

/**
 * Create a group of buttons
 */
const createGroup = (peopleList: HTMLElement) => {
	const popupButtons = peopleList.querySelectorAll<HTMLElement>(
		'[data-popup-button]',
	);

	let buttons: Button[] = [];

	const getButtons = () => buttons;

	const stage = document.createElement('div');
	stage.classList.add('overlay');
	document.body.appendChild(stage);

	if (popupButtons.length > 1) {
		const previousButton = document.createElement('button');
		previousButton.classList.add(
			'overlay__button',
			'overlay__button--previous',
		);

		const nextButton = document.createElement('button');
		nextButton.classList.add('overlay__button', 'overlay__button--next');
		nextButton.setAttribute('data-button-next', '');
		stage.appendChild(nextButton);

		previousButton.setAttribute('data-button-previous', '');
		stage.classList.add('overlay--slider');
		stage.appendChild(previousButton);
	}

	const previousButton = stage.querySelector('[data-button-previous]');

	if (previousButton) {
		previousButton.addEventListener('click', () => {
			const popupEl = document.querySelector('[data-popup]');
			buttons.forEach((button, i) => {
				if (popupEl && popupEl.classList.contains(button.id)) {
					buttons[i - 1]
						? buttons[i - 1].open()
						: buttons[buttons.length - 1].open();
				}
			});
		});
	}

	const nextButton = stage.querySelector('[data-button-next]');

	if (nextButton) {
		nextButton.addEventListener('click', (e) => {
			const popupEl = document.querySelector('[data-popup]');
			buttons.forEach((button, i) => {
				if (popupEl && popupEl.classList.contains(button.id)) {
					buttons[i + 1] ? buttons[i + 1].open() : buttons[0].open();
				}
			});
		});
	}

	popupButtons.forEach((popupButton) => {
		const popup = createPopup(
			popupButton,
			getButtons,
			stage,
			popupButtons.length,
		);
		if (popup) {
			buttons.push(popup);
		}
	});
};

document.addEventListener('DOMContentLoaded', function () {
	const peopleLists =
		document.querySelectorAll<HTMLElement>('[data-people-list]');

	if (peopleLists.length === 0) return;

	peopleLists.forEach(createGroup);
});
